import { z } from "zod";
import { addDaysYmd, berlinHm, berlinMidnight, berlinYmd, isWorkday, nextWorkday } from "./workdays.js";
import type { DeState } from "./holidays.js";

/**
 * Lieferzeit-Anzeige im Shop: aus Bestand bei Logiful, Annahmeschluss, Werktagen (Feiertage) und Laufzeit je Land
 * eine ehrliche Aussage – „Heute bis 14:00 bestellt, Versand noch heute“, Lieferdatum, ggf. „wieder verfügbar ab …“.
 */
export const deliveryWidgetSchema = z.object({
  enabled: z.boolean().default(false),
  /** Annahmeschluss; leer = aus der meistgenutzten Versandart */
  cutoff: z.string().regex(/^\d{2}:\d{2}$/).nullable().default(null),
  /** zusätzliche Bearbeitungstage (z. B. Personalisierung) */
  handlingDays: z.number().int().min(0).max(10).default(0),
  /** Laufzeit in Werktagen je Land (min–max); "*" = alle anderen */
  transit: z.record(z.string().regex(/^([A-Z]{2}|EU|\*)$/), z.tuple([z.number().int().min(0).max(30), z.number().int().min(0).max(30)])).default({ DE: [1, 2], AT: [2, 3], EU: [2, 5], "*": [4, 10] }),
  showCountdown: z.boolean().default(true),
  showDeliveryDate: z.boolean().default(true),
  /** „Nur noch X Stück“ ab dieser Menge (0 = nie) */
  lowStockBelow: z.number().int().min(0).max(1000).default(0),
  /** bei ausverkauften Artikeln „wieder verfügbar ab …“ aus angemeldeten Anlieferungen */
  showRestock: z.boolean().default(true),
});
export type DeliveryWidgetSettings = z.infer<typeof deliveryWidgetSchema>;
export const DEFAULT_DELIVERY_WIDGET: DeliveryWidgetSettings = deliveryWidgetSchema.parse({});

const EU = new Set(["AT", "BE", "BG", "CY", "CZ", "DE", "DK", "EE", "ES", "FI", "FR", "GR", "HR", "HU", "IE", "IT", "LT", "LU", "LV", "MT", "NL", "PL", "PT", "RO", "SE", "SI", "SK"]);
export function transitFor(s: DeliveryWidgetSettings, country: string): [number, number] {
  const c = country.toUpperCase();
  return s.transit[c] ?? (EU.has(c) ? s.transit.EU : undefined) ?? s.transit["*"] ?? [3, 7];
}

const addWorkdays = (ymd: string, n: number, region?: DeState | null) => {
  let d = ymd;
  for (let i = 0; i < n; i++) d = nextWorkday(d, region);
  return d;
};

export type PromiseState = "today" | "next" | "restock" | "unavailable";
export interface DeliveryPromise {
  state: PromiseState;
  /** Versandtag (YYYY-MM-DD) */
  shipDay: string | null;
  deliveryFrom: string | null;
  deliveryTo: string | null;
  /** Minuten bis zum Annahmeschluss (nur state=today) */
  minutesLeft: number | null;
  cutoff: string;
  restockDay: string | null;
  lowStock: number | null;
  text: string;
}

const fmtDay = (ymd: string, lang: "de" | "en") => new Date(`${ymd}T12:00:00Z`).toLocaleDateString(lang === "de" ? "de-DE" : "en-GB", { weekday: "short", day: "2-digit", month: "2-digit", timeZone: "UTC" });
const range = (a: string, b: string, lang: "de" | "en") => (a === b ? fmtDay(a, lang) : `${fmtDay(a, lang)} – ${fmtDay(b, lang)}`);

/** region = Bundesland des versendenden Lagers (Versandtag ohne dessen Feiertage); Laufzeit zum Kunden: bundesweite Feiertage */
export function deliveryPromise(p: { now: Date; available: number; restockAt: Date | null; cutoff: string; settings: DeliveryWidgetSettings; country: string; lang?: "de" | "en"; region?: DeState | null }): DeliveryPromise {
  const lang = p.lang ?? "de";
  const s = p.settings;
  const today = berlinYmd(p.now);
  const [tMin, tMax] = transitFor(s, p.country);
  if (p.available <= 0) {
    const restockDay = p.restockAt && s.showRestock ? berlinYmd(p.restockAt) : null;
    // nach Wareneingang: Einlagerung + Versand am nächsten Werktag
    const after = restockDay ? addWorkdays(restockDay < today ? today : restockDay, 1, p.region) : null;
    return {
      state: after ? "restock" : "unavailable", shipDay: after, deliveryFrom: null, deliveryTo: null, minutesLeft: null, cutoff: p.cutoff, restockDay: after, lowStock: null,
      text: after ? (lang === "de" ? `Wieder lieferbar ab ca. ${fmtDay(after, lang)}` : `Back in stock around ${fmtDay(after, lang)}`) : lang === "de" ? "Derzeit nicht vorrätig" : "Currently out of stock",
    };
  }
  const beforeCutoff = isWorkday(today, p.region) && berlinHm(p.now) < p.cutoff;
  let shipDay = beforeCutoff ? today : nextWorkday(today, p.region);
  if (s.handlingDays) shipDay = addWorkdays(shipDay, s.handlingDays, p.region);
  const deliveryFrom = addWorkdays(shipDay, tMin);
  const deliveryTo = addWorkdays(shipDay, Math.max(tMin, tMax));
  const sameDay = shipDay === today;
  let minutesLeft: number | null = null;
  if (sameDay) {
    const [h, m] = p.cutoff.split(":").map(Number) as [number, number];
    const cutoffAt = berlinMidnight(today).getTime() + (h * 60 + m) * 60_000;
    minutesLeft = Math.max(0, Math.round((cutoffAt - p.now.getTime()) / 60_000));
  }
  const lowStock = s.lowStockBelow && p.available < s.lowStockBelow ? p.available : null;
  const tomorrow = addDaysYmd(today, 1);
  const shipText =
    lang === "de"
      ? sameDay ? `Heute bis ${p.cutoff} Uhr bestellt – Versand noch heute` : shipDay === tomorrow ? "Versand morgen" : `Versand am ${fmtDay(shipDay, lang)}`
      : sameDay ? `Order by ${p.cutoff} today – ships today` : shipDay === tomorrow ? "Ships tomorrow" : `Ships on ${fmtDay(shipDay, lang)}`;
  const deliveryText = s.showDeliveryDate ? (lang === "de" ? ` · Lieferung voraussichtlich ${range(deliveryFrom, deliveryTo, lang)}` : ` · Estimated delivery ${range(deliveryFrom, deliveryTo, lang)}`) : "";
  return { state: sameDay ? "today" : "next", shipDay, deliveryFrom, deliveryTo, minutesLeft, cutoff: p.cutoff, restockDay: null, lowStock, text: shipText + deliveryText };
}
